import { useEffect, useState } from 'react';
import get from 'lodash.get';
import { epgTheme } from './epg-theme.ts';
import { useEpg, type Program, type Channel } from 'planby-mit';
import { isValidUrl } from '~/utils/validations.ts';
import logger from '~/services/logger';
import type { Feed } from '~/services/layout/index.ts';
import { getEpgImage, isEmptyArray } from '~/services/layout/layout.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import { getNormalizedEpgDateTime } from './utils.ts';

const EPG_FETCH_TIMEOUT = 30_000;
const EPG_FETCH_REVALIDATE_TIMEOUT = 120_000;

export function useEpgResources({
  uiComponent,
  feed,
  itemHeight,
  channelSidebarWidth,
  currentTime,
  setCurrentTime,
}: {
  uiComponent: any;
  feed: Feed;
  itemHeight: number;
  channelSidebarWidth: number;
  currentTime: string;
  setCurrentTime: (date: string) => void;
}): {
  getEpgProps: any;
  getLayoutProps: any;
  isLoading: boolean;
  latestDate: string;
} {
  const programsFeedUrl: string = uiComponent?.data?.programs_source?.source;

  const mappingProperties = getMappingProperty(uiComponent);

  const { channels, epgProgramsUrls, channelsUuIds } =
    getChannelsAndProgramsUrls({
      channelsFeed: feed,
      programsFeedUrl,
      mappingProperties,
    });

  const [epg, setEpg] = useState<Program[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { getEpgProps, getLayoutProps } = useEpg({
    channels,
    epg,
    dayWidth: 7200,
    sidebarWidth: channelSidebarWidth,
    itemHeight,
    isSidebar: true,
    isTimeline: true,
    isLine: true,
    isBaseTimeFormat: true,
    startDate: currentTime,
    theme: epgTheme(uiComponent.styles),
  });

  useEffect(() => {
    if (!isValidUrl(programsFeedUrl)) {
      logger.info('Invalid programs feed url');
      return;
    }

    const programsTitleKey: string =
      uiComponent?.styles?.epg_item_label_1_data_key || 'title';

    (async () => {
      setIsLoading(true);

      const response = await requestJsonData({
        url: `/api/epg-programs`,
        method: 'post',
        body: JSON.stringify({
          epgProgramsUrls: epgProgramsUrls || [],
          channelsUuIds: channelsUuIds || [],
          programsTitleKey,
        }),
        timeout: EPG_FETCH_TIMEOUT,
        sessionStorageKey: `epg-programs-${programsFeedUrl}`,
        sessionStorageRevalidate: EPG_FETCH_REVALIDATE_TIMEOUT,
      });

      if (response?.error || isEmptyArray(response)) {
        logger.error('Internal EPG api 500');
        setIsLoading(false);
        return;
      }

      const lastEndedAndUpcomingPrograms =
        getLastEndedAndUpcomingPrograms(response);

      const earliestDate = findEarliestOrLatestDateInPrograms(
        lastEndedAndUpcomingPrograms,
        'earliest'
      );

      if (earliestDate) {
        setCurrentTime(getNormalizedEpgDateTime({ isoDate: currentTime }));
      }

      setEpg(lastEndedAndUpcomingPrograms);

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    const isToday = getIsToday(currentTime);

    if (isToday) return;

    const scrollBox = document.querySelector('[class*="-ScrollBox"]');

    scrollBox?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
  }, [currentTime]);

  const latestDate = findEarliestOrLatestDateInPrograms(epg, 'latest');

  return {
    getEpgProps,
    getLayoutProps,
    isLoading: isLoading || !latestDate,
    latestDate: latestDate || '',
  };
}

/**
 * Gets the EPG mapping properties from the uiComponent.
 * @param uiComponent the uiComponent.
 * @returns an object with mapping properties keyed by their placeholders, or undefined.
 */
export function getMappingProperty(
  uiComponent: any
): Record<string, string> | undefined {
  try {
    const programsFeedUrl: string = uiComponent.data.programs_source.source;
    const mapping = uiComponent?.data?.programs_source?.mapping;

    if (!mapping) throw new Error('No mapping found');

    // Find all keys in the format {{key}}
    const keyRegex = /\{\{([^}]+)\}\}/g;

    let match;

    const mappingProperties: Record<string, string> = {};
    let hasValidMappings = false;

    while ((match = keyRegex.exec(programsFeedUrl)) !== null) {
      const key = match[1];

      if (typeof key !== 'string') continue;

      const mappingProperty: string | undefined = mapping?.[key]?.property;

      if (mappingProperty && mappingProperty !== '') {
        mappingProperties[key] = mappingProperty;
        hasValidMappings = true;
      }
    }

    return hasValidMappings ? mappingProperties : undefined;
  } catch (error: any) {
    logger.info(`getMappingProperty: ${error.message}`);
    return undefined;
  }
}

/**
 * Finds the earliest date minus 30min in the programs array.
 * @param programs the programs array.
 * @returns the earliest date in the programs array as a string value in ISO format.
 */
export function findEarliestOrLatestDateInPrograms(
  programs: Program[],
  mode: 'earliest' | 'latest'
): string | null {
  try {
    if (programs.length === 0) throw new Error('No programs');

    let foundDate = programs[0].since;

    programs.forEach((program: Program) => {
      const isDateFound =
        mode === 'earliest'
          ? new Date(program.since) < new Date(foundDate)
          : new Date(program.since) > new Date(foundDate);

      if (!isDateFound) return;

      foundDate = program.since;
    });

    return `${foundDate}`;
  } catch (error) {
    logger.error(`findEarliestOrLatestDateInPrograms: ${error}`);
    return null;
  }
}

/**
 * Returns an array of EPG programs that includes:
 *   - The most recent program that ended before now (if any), and
 *   - All programs that are ongoing or scheduled for the future (i.e., their 'till' is >= now).
 *
 * This is useful for EPG timelines where you want to show the current and upcoming programs,
 * but also want to include the last finished program for context (e.g., to show what just ended).
 *
 * @param programs Array of EPG Program objects, each with 'since' and 'till' ISO date strings.
 * @returns Array of Program objects: [lastPastItem?, ...futureOrOngoing]
 */
function getLastEndedAndUpcomingPrograms(programs: Program[]): Program[] {
  try {
    const now = new Date();

    const pastItems = programs.filter((item) => new Date(item.till) < now);

    let lastPastItem: Program | null = null;

    if (pastItems.length > 0) {
      lastPastItem = pastItems.reduce((latest, item) =>
        new Date(item.till) > new Date(latest.till) ? item : latest
      );
    }

    const futureOrOngoing = programs.filter(
      (item) => new Date(item.till) >= now
    );

    return lastPastItem ? [lastPastItem, ...futureOrOngoing] : futureOrOngoing;
  } catch (error: any) {
    logger.info(`Error in getLastEndedAndUpcomingPrograms: ${error.message}`);
    return programs || [];
  }
}

type NormalizedChannels = {
  logo: string;
  uuid: string;
}[];

function getChannelsAndProgramsUrls({
  channelsFeed,
  programsFeedUrl,
  mappingProperties,
}: {
  channelsFeed: any;
  programsFeedUrl: string;
  mappingProperties: Record<string, string> | undefined;
}): {
  channels: any;
  channelsUuIds: string[];
  epgProgramsUrls: string[];
} {
  try {
    if (!channelsFeed) throw new Error('No channels data found');

    if (channelsFeed?.entry?.length === 0) {
      throw new Error('No channels entry data found in the EPG feed');
    }

    const channels: NormalizedChannels = normalizeChannelsFeed(
      channelsFeed.entry!
    );

    const channelsUuIds: string[] = [];
    const epgProgramsUrls: string[] = [];

    channelsFeed.entry.forEach((channelEntry: any) => {
      const channelId: string = channelEntry.id;
      channelsUuIds.push(channelId);

      let singleChannelProgramsUrl = programsFeedUrl;

      // Replace all {{key}} placeholders in the URL with their values from the channel entry
      const keyRegex = /\{\{([^}]+)\}\}/g;
      let match;
      let hasReplaced = false;

      while ((match = keyRegex.exec(programsFeedUrl)) !== null) {
        const key = match[1];
        let value = '';

        if (mappingProperties && mappingProperties[key]) {
          // Use mapping property if available
          value = get(channelEntry, mappingProperties[key]) || '';
        } else {
          // Fallback to direct property
          value = get(channelEntry, key) || '';
        }

        singleChannelProgramsUrl = singleChannelProgramsUrl.replace(
          `{{${key}}}`,
          value
        );
        hasReplaced = true;
      }

      if (keyRegex.test(singleChannelProgramsUrl)) {
        logger.info(
          `Some placeholders in programs URL could not be replaced for channel ID: ${channelEntry.id}`
        );
      }

      if (!hasReplaced) {
        logger.info('No keys found to replace in programs URL');
      }

      epgProgramsUrls.push(singleChannelProgramsUrl);
    });

    return {
      channels,
      epgProgramsUrls,
      channelsUuIds,
    };
  } catch (error: any) {
    logger.info(`Error fetching epg channels: ${error.message}`);
    return {
      channels: [],
      channelsUuIds: [],
      epgProgramsUrls: [],
    };
  }
}

/**
 * Normalizes the channels from the EPG feed.
 * @param channelsEntry the channels entry from the EPG feed.
 * @returns the normalized channels from the EPG feed.
 */
export function normalizeChannelsFeed(channelsEntry: any): NormalizedChannels {
  try {
    const res: any = [];

    channelsEntry.forEach((channel: Channel) => {
      const logo: string = getEpgImage(channel, 'image_base');
      const uuid: string = channel?.id;

      if (!uuid) return;

      res.push({ logo, uuid });
    });

    return res;
  } catch (error) {
    return [];
  }
}

function getIsToday(dateString: string): boolean {
  const inputDate = new Date(dateString);
  const today = new Date();

  return (
    inputDate.getFullYear() === today.getFullYear() &&
    inputDate.getMonth() === today.getMonth() &&
    inputDate.getDate() === today.getDate()
  );
}
